import { computed, ref } from 'vue';
import { getCredentials } from '@/utils/auth';

const states = new Map();

const userKey = () => {
  const user = getCredentials() || {};
  return String(user.id || user.username || 'anonymous');
};

const storageKey = () => `dig wave:workspace:${userKey()}`;

const emptyState = () => ({ pinned: [], recent: [], usage: {} });

const readState = () => {
  try {
    return { ...emptyState(), ...JSON.parse(localStorage.getItem(storageKey()) || '{}') };
  } catch {
    return emptyState();
  }
};

const stateForUser = () => {
  const key = storageKey();
  if (!states.has(key)) states.set(key, ref(readState()));
  return states.get(key);
};

export function useWorkspacePreferences() {
  const state = stateForUser();
  const persist = () => localStorage.setItem(storageKey(), JSON.stringify(state.value));

  const pinned = computed(() => state.value.pinned || []);
  const recent = computed(() => state.value.recent || []);
  const usage = computed(() => state.value.usage || {});
  const isPinned = (path) => pinned.value.some((item) => item.path === path);

  const togglePinned = (item) => {
    const path = String(item.path || '');
    if (!path) return;
    state.value.pinned = isPinned(path)
      ? pinned.value.filter((entry) => entry.path !== path)
      : [
          { path, label: item.label, icon: item.icon || 'pi pi-link', permission: item.permission },
          ...pinned.value
        ].slice(0, 8);
    persist();
    trackUsage('pin_toggle', path);
  };

  const recordVisit = (item) => {
    const path = String(item.path || '');
    if (!path || path === '/') return;
    state.value.recent = [
      { path, label: item.label || path, visitedAt: new Date().toISOString() },
      ...recent.value.filter((entry) => entry.path !== path)
    ].slice(0, 6);
    persist();
    trackUsage('page_visit', path);
  };

  const trackUsage = (event, target = 'general') => {
    const key = `${event}:${target}`;
    const previous = state.value.usage?.[key] || {};
    state.value.usage = {
      ...state.value.usage,
      [key]: { count: (previous.count || 0) + 1, lastUsedAt: new Date().toISOString() }
    };
    persist();
  };

  return { pinned, recent, usage, isPinned, togglePinned, recordVisit, trackUsage };
}
